/** Morning Howl board helpers (pure; MorningHowlPage renders them). */
import type { HowlBoard } from '../services/howlApi';

export type MemberStatus = 'now' | 'done' | 'out' | 'waiting';

export function memberStatus(board: HowlBoard, memberId: number): MemberStatus {
  if (board.current_member_id === memberId && !board.finished) return 'now';
  if (board.turns.some((t) => t.member_id === memberId)) return 'done';
  if (board.out_member_ids.includes(memberId)) return 'out';
  return 'waiting';
}

/** Turn number (1-based) for someone who has gone, else null. */
export function turnNumber(board: HowlBoard, memberId: number): number | null {
  return board.turns.find((t) => t.member_id === memberId)?.position ?? null;
}

export function nameOf(board: HowlBoard, memberId: number | null | undefined): string {
  return board.members.find((m) => m.id === memberId)?.name ?? '';
}

/** "Jonny and Emma", "Jonny, Emma and Carly". */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Who still counts today (everyone not out) and how many have gone. */
export function progress(board: HowlBoard): { done: number; total: number } {
  return { done: board.turns.length, total: board.members.length - board.out_member_ids.length };
}

export function isStayingAfter(board: HowlBoard, memberId: number): boolean {
  return board.stay_after.some((s) => s.member_id === memberId);
}

/** Roster textarea → names: one per line, trimmed, blank lines dropped. */
export function parseRoster(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Roster textarea line for a member: "Name <email>" so emails survive edits. */
export function rosterLine(m: { name: string; email?: string | null }): string {
  return m.email ? `${m.name} <${m.email}>` : m.name;
}

export const calendarOk = (status: string | null | undefined): boolean => !!status && status.startsWith('ok');
